/**
 * Resend the customer packet, from the job page (#1545).
 *
 * The problem this removes: once a job left close-out and its packet had gone
 * out, emailing it again meant REOPENING the job and walking the whole
 * close-out loop a second time — for a customer who just wanted their report
 * forwarded. The send itself never needed any of that. The endpoint under this
 * dialog is the same one close-out uses and has always accepted a finished job
 * (`officeReviewWorkspace.ts` — `alreadyApproved`), so this is a front door,
 * not a second sender.
 *
 * Three rules this screen is built on, each a decision, not an accident:
 *
 *  1. **It only exists after a first send.** The caller gates on that; see
 *     `ResendPacketButton`. The FIRST send flips the job to completed, issues
 *     the draft invoice and can auto-charge a card on file — that belongs in
 *     close-out behind those gates. A resend can do none of it (`wasDraft` is
 *     already false in `customerPacketSend`), which is exactly what makes a
 *     one-click button safe here.
 *  2. **No "are you sure".** One press sends. The live footer count is the
 *     check the office reads first, and it restates itself as chips change.
 *     Close-out's own confirm (#230) stays where it is: that one is a first
 *     send, and it can move money.
 *  3. **What you read is what sends.** The subject and message sit open and
 *     editable, pre-filled with the standard wording — not hidden behind a
 *     disclosure, because an office that can't see the words won't trust the
 *     button.
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DroppedAttachmentsWarning, type DroppedAttachment } from "@/components/dropped-attachments-warning";
import {
    StaffOnlySendGate,
    noCustomerEmailRefusal,
    type NoCustomerEmailRefusal,
} from "@/components/staff-only-send-gate";
import { SentAnotherWayDialog } from "@/components/sent-another-way-dialog";
import {
    SendRecipientEditor,
    summarizeSendPlan,
    type PacketDoc,
    type PlanRecipient,
} from "@/components/send-recipient-editor";
import { Loader2, Mail, RotateCcw } from "lucide-react";
import { PacketNotReadyNotice } from "@/components/packet-not-ready-notice";
import { packetBlock, usePacketReadiness, type PacketBlock } from "@/lib/use-packet-readiness";

const DOC_LABEL: Record<PacketDoc, string> = {
    report: "report",
    invoice: "invoice",
    estimate: "estimate",
    photos: "photos",
};
const DOC_ORDER: PacketDoc[] = ["report", "invoice", "estimate", "photos"];

/**
 * "3 emails · report ×2, invoice ×1" — the sentence that replaces a confirm
 * dialog, so it has to be exactly true. The email COUNT comes from
 * `summarizeSendPlan` and nowhere else (#833: two surfaces once counted the
 * same Cintas send differently, one counting plan rows and one counting the
 * server's Cc grouping). The per-document tallies count recipients, which is
 * the honest unit — three people receiving the report is "report ×3" even when
 * they all ride one shared thread.
 */
export function describeSend(plan: ReadonlyArray<PlanRecipient>): string {
    const { emailCount } = summarizeSendPlan(plan);
    const tally = new Map<PacketDoc, number>();
    for (const r of plan) for (const d of r.docs) tally.set(d, (tally.get(d) ?? 0) + 1);
    const docs = DOC_ORDER.filter((d) => (tally.get(d) ?? 0) > 0).map((d) => `${DOC_LABEL[d]} ×${tally.get(d)}`);
    const emails = `${emailCount} email${emailCount === 1 ? "" : "s"}`;
    return docs.length > 0 ? `${emails} · ${docs.join(", ")}` : emails;
}

/**
 * The footer line when the packet can't go out (#1569).
 *
 * This line replaces the email count, and it is the only thing between the
 * office and a dead button — so it says what is missing, never just "can't
 * send". A disabled control with no reason is how you get a phone call.
 */
export function blockedSummary(block: PacketBlock | null): string {
    if (!block) return "";
    if (block.kind === "work_order") return "Can\u2019t send yet \u2014 no completed work order";
    const short = block.total - block.filed;
    return block.portal
        ? `Can\u2019t send yet \u2014 waiting on the city for ${short} of ${block.total}`
        : `Can\u2019t send yet \u2014 ${short} of ${block.total} reports missing`;
}

/** "today at 2:14 PM" / "18 Sep at 2:14 PM" — a parked packet's age is the
 *  thing the office judges "should I chase this" by, so same-day gets the
 *  friendlier form. */
export function formatQueuedAt(iso: string | null | undefined, now: Date = new Date()): string {
    if (!iso) return "earlier";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "earlier";
    const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    const sameDay = d.toDateString() === now.toDateString();
    return sameDay
        ? `today at ${time}`
        : `${d.toLocaleDateString(undefined, { day: "numeric", month: "short" })} at ${time}`;
}

/** What actually happened to a resend, decided from the endpoint's response. */
export type ResendOutcome =
    { kind: "held"; message: string } | { kind: "partial"; message: string } | { kind: "sent"; message: string };

/**
 * Read the endpoint's response honestly (#1549 follow-up, reported live
 * 2026-09-17).
 *
 * `approve-and-send` can return **200 having sent nothing**. When a recipient
 * carries the report chip and the job's official reports aren't all filed, the
 * whole plan is parked (`parkPacketSend`) and `sendCustomerPacket` is never
 * called — no email, no ledger row, no audit event. This dialog originally
 * checked only `partial`/`failed_recipients`/`dropped_recipients`, so a parked
 * resend fell through to the success branch and reported a delivery that had
 * not happened. On a Starbucks job awaiting 4-of-6 filings that produced a
 * green "Packet resent" and then nothing, anywhere — the precise silent
 * non-delivery this feature exists to prevent, committed by the feature itself.
 *
 * Held is checked FIRST and deliberately: it is not a degraded send, it is no
 * send at all, and the office needs to know the difference.
 *
 * Pure so the three branches can be tested without rendering.
 */
export function resendOutcome(
    res: {
        packet_held?: boolean;
        packet_waiting?: {
            reportsFiled?: number;
            reportsTotal?: number;
            waitingOnReports?: boolean;
            waitingOnWorkOrder?: boolean;
        } | null;
        partial?: boolean;
        failed_recipients?: unknown;
        dropped_recipients?: unknown;
    },
    emailCount: number,
    onlyMissed: boolean,
): ResendOutcome {
    if (res?.packet_held) {
        const w = res.packet_waiting ?? null;
        const parts = [
            w?.waitingOnReports ? `the official report (${w.reportsFiled ?? 0} of ${w.reportsTotal ?? 0} filed)` : null,
            w?.waitingOnWorkOrder ? "the completed work order" : null,
        ].filter(Boolean);
        return {
            kind: "held",
            message:
                `Nothing sent yet — this packet is waiting on ${parts.join(" and ") || "release"}. ` +
                "It releases automatically as one email once that's in, and nothing went out just now.",
        };
    }

    const failed: string[] = Array.isArray(res?.failed_recipients) ? (res.failed_recipients as string[]) : [];
    const dropped = Array.isArray(res?.dropped_recipients)
        ? (res.dropped_recipients as Array<{ recipient: string }>)
        : [];
    if (res?.partial || failed.length > 0 || dropped.length > 0) {
        const parts: string[] = [];
        if (failed.length > 0) parts.push(`${failed.length} delivery failed: ${failed.join(", ")}`);
        if (dropped.length > 0) {
            parts.push(
                `${dropped.length} received nothing (their documents weren't ready): ` +
                    dropped.map((d) => d.recipient).join(", "),
            );
        }
        return { kind: "partial", message: `Sent, but not to everyone — ${parts.join("; ")}.` };
    }

    return {
        kind: "sent",
        message: onlyMissed
            ? "Resent to who missed it."
            : `Packet resent — ${emailCount} email${emailCount === 1 ? "" : "s"}.`,
    };
}

interface ResendPacketDialogProps {
    open: boolean;
    onClose: () => void;
    jobId: string;
    customerId: string | null;
    /** Pre-filled subject line; the office edits it freely before sending. */
    defaultSubject: string;
    /** Opens seeded with only the recipients who missed the last send. */
    startInMissedMode?: boolean;
    /** #1569: the job page passes its own `openChapter("paperwork")` so the
     *  fix-it link scrolls instead of navigating away from the page you are
     *  already on. Absent elsewhere; the notice then links to the job. */
    onGoToPaperwork?: () => void;
    onSent?: () => void;
}

export function ResendPacketDialog({
    open,
    onClose,
    jobId,
    customerId,
    defaultSubject,
    startInMissedMode = false,
    onSent,
    onGoToPaperwork,
}: ResendPacketDialogProps) {
    const qc = useQueryClient();
    const [plan, setPlan] = useState<PlanRecipient[]>([]);
    const [missedCount, setMissedCount] = useState(0);
    const [onlyMissed, setOnlyMissed] = useState(startInMissedMode);
    const [subject, setSubject] = useState(defaultSubject);
    const [message, setMessage] = useState("");
    const [dropped, setDropped] = useState<DroppedAttachment[] | null>(null);
    const [staffOnly, setStaffOnly] = useState<NoCustomerEmailRefusal | null>(null);
    // "Already sent another way" (2026-10-07), from the gate below.
    const [otherWay, setOtherWay] = useState(false);

    // #1569: what would stop this packet going out, read BEFORE the press.
    // Only while the dialog is open — a closed dialog has nothing to gate.
    const readinessQ = usePacketReadiness(jobId, open);
    const block = packetBlock(readinessQ.data);

    // Reopening must not inherit the last visit's edits — the office would be
    // sending a message they wrote for a different reason and can no longer see
    // a reason for.
    useEffect(() => {
        if (!open) return;
        setSubject(defaultSubject);
        setMessage("");
        setDropped(null);
        setStaffOnly(null);
        setOnlyMissed(startInMissedMode);
    }, [open, defaultSubject, startInMissedMode]);

    // Every document the plan touches is "in play" for the editor: a resend
    // re-sends what this job has, and the editor itself strikes out anything
    // the job doesn't (no estimate → nobody can be given one).
    const include = useMemo(() => ({ invoices: true, estimates: true, reports: true, photos: true }), []);

    const summary = describeSend(plan);
    const nothingToSend = plan.length === 0 || plan.every((r) => r.docs.length === 0);
    // A resend never queues (Corbin, 2026-09-21). While the packet is short a
    // report or its work order, the button is simply dead — the server refuses
    // it too (PACKET_NOT_READY), so a stale dialog can't slip one through.
    const blocked = block !== null;

    // The response shape this dialog actually reads. `packet_held` is part of
    // the endpoint's contract, not an extra — see resendOutcome.
    const send = useMutation<
        Parameters<typeof resendOutcome>[0],
        ApiError,
        { confirmDropped?: boolean; staffOnlyConfirmed?: boolean }
    >({
        mutationFn: (opts) =>
            api.post(`/api/office-review/jobs/${jobId}/approve-and-send`, {
                // A resend carries whatever the chips say; the server re-derives
                // what actually exists, so these stay open and the plan decides.
                invoices: true,
                estimates: true,
                reports: true,
                photos: true,
                // The server refuses a "resend" of a packet that never went out,
                // so a stale or mis-gated button can't become the first send.
                resendOnly: true,
                plan,
                emailSubject: subject.trim(),
                emailMessage: message.trim(),
                ...(opts?.confirmDropped ? { confirmDroppedAttachments: true } : {}),
                ...(opts?.staffOnlyConfirmed ? { staffOnlyConfirmed: true } : {}),
            }),
        onSuccess: (res) => {
            const { emailCount } = summarizeSendPlan(plan);
            const outcome = resendOutcome(res, emailCount, onlyMissed);
            // Held and partial are BOTH "don't claim a clean send". Held is the
            // louder of the two: nothing went out at all.
            if (outcome.kind === "held") toast.warning(outcome.message, { duration: 14000 });
            else if (outcome.kind === "partial") toast.error(outcome.message, { duration: 14000 });
            else toast.success(outcome.message);

            qc.invalidateQueries({ queryKey: ["send-history", jobId] });
            qc.invalidateQueries({ queryKey: ["send-preview", jobId] });
            qc.invalidateQueries({ queryKey: ["job", jobId] });
            qc.invalidateQueries({ queryKey: ["job-activity", jobId] });
            setDropped(null);
            setStaffOnly(null);
            onSent?.();
            onClose();
        },
        onError: (e) => {
            // CE-1: selected files that won't ride. Show exactly which, and make
            // the office say "send anyway" — never drop them quietly.
            if (
                e?.status === 409 &&
                (e.body as { error?: string } | undefined)?.error === "DROPPED_ATTACHMENTS_UNCONFIRMED"
            ) {
                const body = e.body as { droppedAttachments?: DroppedAttachment[] };
                setDropped(body.droppedAttachments ?? []);
                return;
            }
            // Every recipient is ours: say so and let the office choose.
            const refusal = noCustomerEmailRefusal(e);
            if (refusal) {
                setStaffOnly(refusal);
                return;
            }
            const body = e?.body as { message?: string; error?: string } | undefined;
            // #1569: the server refused because the packet isn't complete. The
            // dialog's own button should already have been dead, so this is a
            // stale read — refresh it so the notice appears and the button
            // matches, instead of leaving a live button that keeps failing.
            if (e?.status === 422 && body?.error === "PACKET_NOT_READY") {
                qc.invalidateQueries({ queryKey: ["packet-readiness", jobId] });
                toast.warning(body.message ?? "Nothing was sent — this packet isn't complete yet.", {
                    duration: 14000,
                });
                return;
            }
            toast.error(body?.message ?? "The packet couldn't be resent. Nothing was sent.", { duration: 10000 });
        },
    });

    return (
        <>
            <SentAnotherWayDialog
                jobId={jobId}
                open={otherWay}
                onClose={() => setOtherWay(false)}
                onSaved={() => onClose()}
            />
            <Dialog open={open} onClose={onClose} title="Resend packet" className="max-w-2xl">
                <div className="space-y-4">
                    {!customerId ? (
                        <p className="text-sm text-amber-700 dark:text-amber-300">
                            This job has no customer on it, so there is nobody to send to.
                        </p>
                    ) : (
                        <>
                            {block && (
                                <PacketNotReadyNotice
                                    block={block}
                                    jobId={jobId}
                                    consequence="blocked"
                                    queued={
                                        readinessQ.data?.held
                                            ? {
                                                  at: formatQueuedAt(readinessQ.data.parked_at),
                                                  by: null,
                                              }
                                            : null
                                    }
                                    onGoToPaperwork={onGoToPaperwork}
                                />
                            )}
                            <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-[12.5px] text-sky-900 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-100">
                                <strong>Nothing is re-issued and no card is charged.</strong> Resending emails the same
                                documents again — an invoice that is already open or paid stays exactly as it is.
                            </div>

                            <div>
                                <div className="mb-2 flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-1.5 text-sm font-medium">
                                        <Mail className="h-3.5 w-3.5 text-foreground/80" /> Who gets it
                                    </div>
                                    {missedCount > 0 &&
                                        (onlyMissed ? (
                                            <button
                                                type="button"
                                                onClick={() => setOnlyMissed(false)}
                                                className="rounded-md border border-border bg-muted px-2 py-1 text-[11px] font-semibold text-foreground/70 hover:bg-muted/70"
                                            >
                                                ← Back to everyone
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => setOnlyMissed(true)}
                                                className="rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-800 hover:bg-amber-100 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200"
                                            >
                                                ↻ Resend to who missed it ({missedCount})
                                            </button>
                                        ))}
                                </div>
                                {onlyMissed && missedCount > 0 && (
                                    <p className="mb-2 text-[11px] text-amber-700 dark:text-amber-300">
                                        Showing only the {missedCount} recipient{missedCount === 1 ? "" : "s"} who
                                        didn&apos;t get the last send. Nobody who already received it is emailed again.
                                    </p>
                                )}
                                <SendRecipientEditor
                                    jobId={jobId}
                                    customerId={customerId}
                                    include={include}
                                    onChange={setPlan}
                                    onlyMissed={onlyMissed}
                                    onMissedCountChange={setMissedCount}
                                />
                            </div>

                            <div className="space-y-2">
                                <div className="text-sm font-medium">Subject &amp; message</div>
                                <Input
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    maxLength={300}
                                    aria-label="Email subject"
                                    placeholder="Subject"
                                />
                                <Textarea
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    maxLength={5000}
                                    rows={4}
                                    aria-label="Email message"
                                    placeholder="Leave blank to use the standard wording."
                                />
                                <p className="text-[11px] text-foreground/60">
                                    What you read here is what sends. Leave the message blank and the standard wording
                                    goes out as it always does.
                                </p>
                            </div>

                            {dropped && dropped.length > 0 && (
                                <DroppedAttachmentsWarning
                                    dropped={dropped}
                                    isPending={send.isPending}
                                    onSendAnyway={() =>
                                        send.mutate({ ...(send.variables ?? {}), confirmDropped: true })
                                    }
                                    onCancel={() => setDropped(null)}
                                />
                            )}
                            {staffOnly && (
                                <StaffOnlySendGate
                                    refusal={staffOnly}
                                    isPending={send.isPending}
                                    onSendStaffOnly={() => {
                                        setStaffOnly(null);
                                        send.mutate({ ...(send.variables ?? {}), staffOnlyConfirmed: true });
                                    }}
                                    onSentAnotherWay={() => {
                                        setStaffOnly(null);
                                        setOtherWay(true);
                                    }}
                                    onCancel={() => setStaffOnly(null)}
                                />
                            )}
                        </>
                    )}
                </div>

                {/* The footer count is the safety check — there is no confirm step
                after this, by design. It must always describe the CURRENT plan. */}
                <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t pt-3">
                    <span className="text-[11.5px] text-foreground/60" data-testid="resend-summary">
                        {blocked ? blockedSummary(block) : nothingToSend ? "Nothing selected to send." : summary}
                    </span>
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={onClose} disabled={send.isPending}>
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            onClick={() => send.mutate({})}
                            disabled={!customerId || nothingToSend || blocked || send.isPending}
                        >
                            {send.isPending ? (
                                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                            ) : (
                                <RotateCcw className="mr-1 h-3.5 w-3.5" />
                            )}
                            Resend packet
                        </Button>
                    </div>
                </div>
            </Dialog>
        </>
    );
}
